import type { ReactNode } from 'react';
import { Logo } from './ui.js';

// Free Pexels photo (modern office / analytics).
const HERO =
  'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1400';

/** Split-screen auth shell: a real hero photo + brand on the left, form on the right. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen bg-white dark:bg-neutral-950">
      {/* Left — hero photo (visible md and up) */}
      <div className="relative hidden md:block md:w-1/2 lg:w-[55%]">
        <img
          src={HERO}
          alt="Team reviewing commission analytics"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        {/* Light dim so the photo reads as a photo, + a bottom gradient for text legibility. */}
        <div className="absolute inset-0 bg-neutral-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/95 via-neutral-950/35 to-transparent" />
        <div className="absolute -left-20 top-24 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />

        <div className="relative flex h-full flex-col justify-between p-10 text-white xl:p-14">
          <span className="flex items-center gap-2.5 drop-shadow">
            <Logo />
          </span>
          <div className="max-w-md">
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-emerald-200 ring-1 ring-white/15 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Commission intelligence
            </span>
            <h2 className="text-3xl font-semibold leading-tight tracking-tight xl:text-4xl">
              Every carrier statement, unified into one auditable book.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-white/80">
              Track fallout, reappearance, and commission deltas across months — scored fast and
              accurately, month over month and quarter over quarter.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-white/85">
              <li className="flex items-center gap-2.5">
                <Check /> Green / yellow / red scoring, every month
              </li>
              <li className="flex items-center gap-2.5">
                <Check /> Compare any two periods, any carrier
              </li>
              <li className="flex items-center gap-2.5">
                <Check /> AI assistant, full API &amp; signed webhooks
              </li>
            </ul>
          </div>
          <p className="text-xs text-white/45">
            © {new Date().getFullYear()} CommissionSight · Patent Pending
          </p>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex w-full items-center justify-center px-6 py-12 md:w-1/2 lg:w-[45%]">
        <div className="w-full max-w-sm">
          <div className="mb-8 md:hidden">
            <Logo />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle ? (
            <p className="mt-1 mb-6 text-sm text-neutral-500">{subtitle}</p>
          ) : (
            <div className="mb-6" />
          )}
          {children}
          {footer && <div className="mt-6 text-sm text-neutral-500">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function Check() {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="text-emerald-300"
      >
        <path
          d="M5 12l4 4 10-10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
